import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider } from '@mui/material';
import { BetaWidget } from '@insights-ltd/design-library/beta';
import { BetaProvider } from '@insights-ltd/design-library/components';
import { getMuiTheme } from '@insights-ltd/design-library/themes';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { FullScreenSpinner } from 'components/FullScreen';
import { Settings } from 'luxon';
import { addError } from 'utils/datadog-error';
import { createRoutes, options } from '~/app/router';
import { BETA_DEFAULT, getBetaAvailable } from './features';
import { ErrorRoute } from './ErrorRoute';

Settings.defaultZone = 'UTC';

const theme = getMuiTheme();
const betaAvailable = getBetaAvailable();
const router = createBrowserRouter(createRoutes(), options());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <BetaProvider betaDefault={BETA_DEFAULT} enabled={getBetaAvailable()}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <Suspense fallback={<FullScreenSpinner />}>
          <ErrorBoundary FallbackComponent={ErrorRoute} onError={addError}>
            {import.meta.env.MODE !== 'test' && betaAvailable && (
              <BetaWidget
                onToggle={() =>
                  router.navigate(null, {
                    replace: true,
                    state: router.state.location.state,
                  })
                }
              />
            )}
            <RouterProvider router={router} />
          </ErrorBoundary>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  </BetaProvider>
);

export default App;
